import { useId } from "react";
import { CheckCircle2, FileText } from "lucide-react";
import type { Credential } from "@/features/credentials/types";
import {
  missingOnboardingDocuments,
  ONBOARDING_DOCUMENT_REQUIREMENTS,
} from "../utils/onboarding-document-requirements";

export function OnboardingDocumentChecklist({
  credentials,
}: {
  credentials?: Credential[];
}) {
  const headingId = useId();
  const missingNames = new Set(
    missingOnboardingDocuments(credentials ?? []).map(({ name }) => name),
  );

  return (
    <section
      aria-labelledby={headingId}
      className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5 sm:p-6"
    >
      <div className="space-y-1">
        <h3 id={headingId} className="font-semibold text-slate-900">
          Documents to upload
        </h3>
        <p className="text-sm leading-relaxed text-slate-500">
          Gather the documents below that apply to you. Upload what you have now
          and add the rest later from your profile.
        </p>
      </div>
      <ul className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {ONBOARDING_DOCUMENT_REQUIREMENTS.map(({ name }) => {
          const saved = !missingNames.has(name);
          return (
            <li key={name} className="flex items-start gap-2 text-sm">
              {saved ? (
                <CheckCircle2
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 text-emerald-600"
                />
              ) : (
                <FileText
                  aria-hidden="true"
                  className="mt-0.5 size-4 shrink-0 text-primary/70"
                />
              )}
              <span className="flex-1 text-slate-700">{name}</span>
              {saved && (
                <span className="shrink-0 text-xs font-medium text-emerald-700">
                  Saved
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {credentials && (
        <p className="text-xs leading-relaxed text-slate-500">
          Each document is marked saved after you review and confirm its
          details.
        </p>
      )}
    </section>
  );
}
